import type { LevelCatalog } from "@highmarch/app";
import { BalanceData, PlayerProfile, UpgradeJob, UpgradeTarget } from "@highmarch/domain";
import { icon } from "../iconsKit";
import { fmtBadgeEta } from "./badges";
import type { BuilderPick } from "./builderPick";
import type { HubFeatures } from "./hubFeatures";
import { SceneLink, SceneTarget } from "./sceneApi";
import { appHeight } from "../appSize";

/** Where the builder chip sits when the scene cannot say where the fortress is: this far down the scene band. */
const CHIP_FALLBACK_Y = 0.62;
/** The builder chip keeps this far above the battle card's top edge (CSS px). */
const CHIP_CARD_GAP = 8;

/**
 * The hub view's side of the 3D scene band (hubView.ts): what the scene shows (scaffold on the building under upgrade,
 * the next tower's ghost), where it is framed, and the chips pinned to what they label. Over SceneLink (sceneApi.ts),
 * so a scene without a method just skips it.
 */
export class HubSceneLink {
  constructor(private readonly scene: SceneLink, private readonly root: HTMLElement, private readonly catalog: LevelCatalog, private readonly profile: PlayerProfile) {}

  private get B(): BalanceData { return this.catalog.balance; }

  /** The building an upgrade job works on (Pets v2: a pet's job is the pup on the lawn). */
  targetOf(job: UpgradeJob): SceneTarget {
    return job.target === UpgradeTarget.Base ? "fortress" : job.target === UpgradeTarget.Pet ? { pet: this.B.pets.list[job.towerIndex]?.id ?? "" } : { tower: this.B.towers[job.towerIndex].id };
  }

  /** Tells the scene what to show: scaffold on the building under upgrade, the ghost of the next tower to unlock. */
  sync(): void {
    const job = [...this.profile.jobs].sort((a, b) => a.endAt - b.endAt)[0];
    this.scene.upgrading(job ? this.targetOf(job) : null);
    const owned = this.catalog.ownedTowers();
    const next = this.B.towers.filter((t, i) => !t.isBase && !owned[i]).sort((a, b) => a.unlockLevel - b.unlockLevel)[0];
    this.scene.ghost(next?.id ?? null);
    const ghost = this.root.querySelector<HTMLElement>(".ghostchip")!;
    ghost.dataset.tower = next?.id ?? "";
    ghost.innerHTML = next ? `${icon("lock", 11)} ${next.name} · Level ${next.unlockLevel}` : "";
  }

  /**
   * Frames the scene between the top bar and the battle card, and pins the chips to what they label: the builder chip
   * on the fortress, the next-tower tag on its ghost. On render and resize; `follow` re-pins them as the shot moves.
   */
  layout(pick: BuilderPick, features: HubFeatures): void {
    const top = this.root.querySelector<HTMLElement>(".topbar")?.getBoundingClientRect().bottom ?? 0;
    const dock = this.root.querySelector<HTMLElement>(".dock")?.getBoundingClientRect().top ?? appHeight();
    this.scene.setFrame(Math.round(top), Math.round(appHeight() - dock));
    this.pinChips(pick, features);
  }

  /**
   * Keeps the chips on what they label while the shot moves (a pinch, a pan), and swallows a click on the hub's buttons
   * that is part of a pinch or a drag on the scene, so a pinch never opens a sheet. Returns the undo.
   */
  follow(pick: () => BuilderPick, features: HubFeatures): () => void {
    const off = this.scene.onLayout(() => this.pinChips(pick(), features));
    const guard = (e: Event) => { if (this.scene.gestureBusy) { e.stopPropagation(); e.preventDefault(); } };
    this.root.addEventListener("click", guard, true);
    return () => { off(); this.root.removeEventListener("click", guard, true); };
  }

  /** The builder chip on the fortress, the next-tower tag on its ghost, the features' chips on theirs. */
  private pinChips(pick: BuilderPick, features: HubFeatures): void {
    this.updateChip(pick);
    features.layoutWidgets();
    const ghost = this.root.querySelector<HTMLElement>(".ghostchip")!;
    const at = ghost.dataset.tower ? this.scene.anchor({ tower: ghost.dataset.tower }) : null;
    ghost.classList.toggle("hidden", !at);
    if (at) ghost.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px) translate(-50%, 8px)`;
  }

  /** The builder chip: a blue timer ring while busy, red "Idle" only when a builder is free and an upgrade is affordable. */
  updateChip(pick: BuilderPick): void {
    const chip = this.root.querySelector<HTMLButtonElement>(".bchip");
    if (!chip) return;
    const show = pick.kind === "busy" || (pick.kind === "idle" && pick.actionable);
    chip.classList.toggle("hidden", !show);
    if (!show) return;
    chip.className = `bchip ${pick.kind}`;
    chip.innerHTML = pick.kind === "busy"
      ? `<span class="ring" style="--p:${Math.round(pick.progress * 100)}%">${icon("builder", 16, "#fff8e6")}</span><span class="t">${fmtBadgeEta(pick.remaining)}</span>`
      : `<span class="ring">${icon("builder", 16, "#fff8e6")}</span><span class="t">Builder idle</span>`;
    const top = this.root.querySelector<HTMLElement>(".topbar")?.getBoundingClientRect().bottom ?? 0;
    const dock = this.root.querySelector<HTMLElement>(".dock")?.getBoundingClientRect().top ?? appHeight();
    const at = this.scene.anchor("fortress") ?? { x: innerWidth / 2, y: top + (dock - top) * CHIP_FALLBACK_Y };
    // the chip stands on the fortress, but never on the battle card: a tall card (FARM, the pre-battle rows) covers the
    // fortress's foot, so the chip rises to sit just above the card's rim
    const y = Math.min(at.y, dock - CHIP_CARD_GAP);
    chip.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(y)}px) translate(-50%, -100%)`;
  }

  /** Jobs that finished while the hub was open: each building celebrates. */
  celebrate(done: readonly UpgradeJob[]): void { for (const j of done) this.scene.celebrate(this.targetOf(j)); }
}
